'use client'

import React, { useRef } from 'react'
import NextLink, { LinkProps as NextLinkProps } from 'next/link'
import { usePathname, useRouter } from 'next/navigation'
import { isSafari } from 'react-device-detect'

export type LinkProps = NextLinkProps & { children?: React.ReactNode, className?: string, target?: string }

const runJavascriptFromString = (script: string) => {
    try {
        (new Function(script))()
    } catch (error) {
        console.error('Error executing JavaScript:', error)
    }
}

const Link = (props: LinkProps) => {
    const router = useRouter()
    const pathName = usePathname()
    const timeoutRef = useRef<number | null>(null)

    const logDebug = (message: string, ...other: unknown[]) => {
        if ((window as any)?.gtap?.debug) {
            console.debug(message, other)
        }
    }

    const transitionOut = (callback: () => void) => {
        const transitionDiv = document.getElementById('page-transition')

        const onAnimationEnd = () => {
            if (transitionDiv) {
                timeoutRef.current = window.setTimeout(() => {
                    transitionDiv.className = transitionDiv.className.replace(' h-page-animate--out', '')
                }, 1000)
                transitionDiv.removeEventListener('animationend', onAnimationEnd)
            }
            callback()
        }

        if (transitionDiv && !transitionDiv.classList.contains('h-page-animate--out')) {
            transitionDiv.className += ' h-page-animate--out'
            transitionDiv.addEventListener('animationend', onAnimationEnd)
        }
    }

    const isSameRoute = (href: string, pathName: string): boolean => {
        if (href.startsWith('/')) { // If the given href is an absolute path...
            return href === pathName
        } else { // ...otherwise if it's a relative path or external link
            const pathNameLastSegment = pathName.substring(pathName.lastIndexOf('/') + 1)

            return href == pathNameLastSegment // If it's an external link, then they aren't the same route anyway and the comparison will fail by default.
        }
    }

    // Handle javascript links separately as React doesn't like them
    if ((props.href as string).startsWith('javascript')) {
        const noHrefProps = { ...props, href: "#", target: props.target || undefined }

        return (
            <NextLink
                {...noHrefProps}
                onClick={
                    (event: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
                        event.preventDefault()
                        runJavascriptFromString((props.href as string).replace('javascript:', ''))
                    }
                }
            >
                {props.children}
            </NextLink>
        )
    }

    const onClick = (event: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
        logDebug('Link clicked: ' + props.href)

        if (props.onClick) {
            logDebug('Calling onClick handler callback')
            props.onClick(event)
        }

        if (!props.href) {
            console.error('No href provided for link')
            return
        }

        if (props.target == '_blank') {
            logDebug('Opening link in new tab')
            return
        }

        logDebug('Preventing default')
        event.preventDefault()

        if ((props.href as string).startsWith('#')) {
            const target = document.querySelector((props.href as string))

            if (target) {
                target.scrollIntoView({
                    behavior: 'smooth'
                })
            }

            return
        }

        const opensInCurrentTab = props.target == '_self' || props.target == undefined
        const isSamePage = isSameRoute(props.href as string, pathName)

        if (opensInCurrentTab && !isSamePage && !isSafari) {
            transitionOut(() => {
                const wasExternal = (props.href as string).startsWith('http')
                if (wasExternal) {
                    // Go straight to the external link
                    logDebug('Navigating to external link: ' + props.href)
                    window.location.href = props.href as string
                } else {
                    logDebug('Pushing new route with transition: ' + props.href, router)
                    router.push(props.href as string)
                }
            })
        } else {
            logDebug('Pushing new route: ' + props.href)
            router.push(props.href as string)
        }
    }

    return <NextLink {...props} target={props.target || undefined} onClick={onClick}>{props.children}</NextLink>
}

export default Link